import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Clock, Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { Empty } from "@/components/Shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DIFF_COLOR = { Beginner: "bg-teal-100 text-teal-800", Intermediate: "bg-secondary/40 text-secondary-foreground", Advanced: "bg-accent/15 text-accent" };

export function InternshipCard({ it }) {
    return (
        <Link to={`/internships/${it.id}`} data-testid={`internship-card-${it.id}`}
            className="card-soft group flex h-full flex-col p-6 transition-transform duration-200 hover:-translate-y-1">
            <div className="flex items-start justify-between gap-3">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{it.domain}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${DIFF_COLOR[it.difficulty] || "bg-muted"}`}>{it.difficulty}</span>
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold leading-snug group-hover:text-primary">{it.title}</h3>
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{it.description}</p>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{it.duration_months} months</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{it.mode}{it.location && it.location !== "Remote" ? ` · ${it.location}` : ""}</span>
            </div>
            <div className="mt-auto flex items-center justify-between border-t border-border/40 pt-4">
                <span className="font-display text-lg font-bold text-primary">${it.fee_usd}<span className="ml-1 text-xs font-normal text-muted-foreground">program fee</span></span>
                <span className="text-xs text-muted-foreground">by {it.posted_by_name}</span>
            </div>
        </Link>
    );
}

function HubContent() {
    const [params] = useSearchParams();
    const [items, setItems] = useState(null);
    const [meta, setMeta] = useState({ domains: [], modes: [], difficulties: [], durations: [2, 6] });
    const [filters, setFilters] = useState({ q: "", domain: "", mode: "", difficulty: "", duration: params.get("duration") || "", max_fee: "" });

    useEffect(() => { api.get("/internships/meta/filters").then((r) => setMeta(r.data)).catch(() => {}); }, []);

    useEffect(() => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
        api.get(`/internships?${q.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, [filters]);

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "all" ? "" : v }));

    return (
        <div className="grid gap-8 lg:grid-cols-4" data-testid="internship-hub">
            <aside className="card-soft h-fit space-y-5 p-6 lg:sticky lg:top-24">
                <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="filter-search" placeholder="Search title, skill..." className="rounded-lg pl-9" value={filters.q} onChange={(e) => set("q", e.target.value)} />
                </div>
                {[
                    { key: "domain", label: "Domain", options: meta.domains },
                    { key: "mode", label: "Mode", options: meta.modes },
                    { key: "difficulty", label: "Difficulty", options: meta.difficulties },
                ].map((f) => (
                    <div key={f.key} className="space-y-2">
                        <Label>{f.label}</Label>
                        <Select value={filters[f.key] || "all"} onValueChange={(v) => set(f.key, v)}>
                            <SelectTrigger data-testid={`filter-${f.key}`} className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                ))}
                <div className="space-y-2">
                    <Label>Duration</Label>
                    <Select value={filters.duration || "all"} onValueChange={(v) => set("duration", v)}>
                        <SelectTrigger data-testid="filter-duration" className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any</SelectItem>
                            <SelectItem value="2">2 months</SelectItem>
                            <SelectItem value="6">6 months</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Max program fee (USD)</Label>
                    <Select value={filters.max_fee || "all"} onValueChange={(v) => set("max_fee", v)}>
                        <SelectTrigger data-testid="filter-fee" className="rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any</SelectItem>
                            <SelectItem value="500">Up to $500</SelectItem>
                            <SelectItem value="1000">Up to $1,000</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button data-testid="filter-clear-button" variant="outline" className="w-full rounded-full" onClick={() => setFilters({ q: "", domain: "", mode: "", difficulty: "", duration: "", max_fee: "" })}>Clear filters</Button>
            </aside>
            <div className="lg:col-span-3">
                {items === null ? <p className="text-sm text-muted-foreground">Loading opportunities...</p>
                : items.length === 0 ? <Empty icon={Briefcase} title="No internships match your filters" hint="Try clearing a filter or two." />
                : (
                    <>
                        <p className="mb-4 text-sm text-muted-foreground" data-testid="internship-count">{items.length} opportunit{items.length === 1 ? "y" : "ies"} found</p>
                        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {items.map((it) => <InternshipCard key={it.id} it={it} />)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function Internships() {
    const { user } = useAuth();
    const content = (
        <div className="mx-auto max-w-7xl">
            <h1 className="font-display text-3xl font-bold tracking-tight">Internship Hub</h1>
            <p className="mt-2 text-base text-muted-foreground">Structured programs from verified faculty and organizations. Program fees are always shown upfront.</p>
            <div className="mt-8"><HubContent /></div>
        </div>
    );
    if (user) return <DashboardLayout title="Internship Hub">{content}</DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14">{content}</div>
            <Footer />
        </div>
    );
}
